import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaKey,
  FaLock,
  FaBuilding,
  FaClock,
  FaCheckCircle,
} from "react-icons/fa";
import axios from "axios";
import styles from "./ArenaOwnershipTransfer.module.css";

const ArenaOwnershipTransfer = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [transferData, setTransferData] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    fetchTransferDetails();
  }, [token]);

  const fetchTransferDetails = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/ownership-transfer/${token}`
      );
      setTransferData(response.data);
      setMessage("");
    } catch (error) {
      console.error("Error fetching transfer details:", error);
      if (error.response && error.response.data && error.response.data.error) {
        setMessage(error.response.data.error);
      } else {
        setMessage("Invalid or expired transfer link.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!verificationCode.trim()) {
      setMessage("Please enter the verification code.");
      return;
    }

    if (verificationCode.length !== 6) {
      setMessage("Verification code must be 6 digits.");
      return;
    }

    if (!newPassword) {
      setMessage("Please enter a new password.");
      return;
    }

    if (newPassword.length < 6) {
      setMessage("Password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/complete-ownership-transfer`,
        {
          token: token,
          verificationCode: verificationCode.trim(),
          newPassword: newPassword,
        }
      );

      setMessage(response.data.message);
      setIsVerified(true);

      // Redirect to arena login after 3 seconds
      setTimeout(() => {
        navigate("/LoginArena");
      }, 3000);
    } catch (error) {
      console.error("Error completing ownership transfer:", error);
      if (error.response && error.response.data && error.response.data.error) {
        setMessage(error.response.data.error);
      } else {
        setMessage(
          "An error occurred while completing the transfer. Please try again."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingBox}>
          <div className={styles.spinner}></div>
          <p>Loading transfer details...</p>
        </div>
      </div>
    );
  }

  if (!transferData) {
    return (
      <div className={styles.container}>
        <div className={styles.errorBox}>
          <h2>Transfer Not Found</h2>
          <p>{message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.transferCard}>
        <div className={styles.header}>
          <FaBuilding className={styles.headerIcon} />
          <h1>Arena Ownership Transfer</h1>
        </div>

        <div className={styles.arenaInfo}>
          <h2>{transferData.arenaName}</h2>
          <p>
            <strong>New Owner:</strong> {transferData.newOwnerEmail}
          </p>
          <p className={styles.expiry}>
            <FaClock className={styles.icon} />
            <strong>Expires:</strong> {formatDate(transferData.expiresAt)}
          </p>
        </div>

        {isVerified ? (
          <div className={styles.successBox}>
            <FaCheckCircle className={styles.successIcon} />
            <h3>Transfer Completed Successfully!</h3>
            <p>{message}</p>
            <p className={styles.redirectMessage}>
              Redirecting to login page in 3 seconds...
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.transferForm}>
            <div className={styles.inputGroup}>
              <label htmlFor="verificationCode">
                <FaKey className={styles.inputIcon} />
                Verification Code (6 digits):
              </label>
              <input
                type="text"
                id="verificationCode"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(
                    e.target.value.replace(/\D/g, "").slice(0, 6)
                  )
                }
                placeholder="Enter 6-digit code from email"
                maxLength="6"
                required
                className={styles.inputField}
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="newPassword">
                <FaLock className={styles.inputIcon} />
                New Password:
              </label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                minLength="6"
                required
                className={styles.inputField}
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="confirmPassword">
                <FaLock className={styles.inputIcon} />
                Confirm New Password:
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                minLength="6"
                required
                className={styles.inputField}
              />
            </div>

            <button
              type="submit"
              className={styles.transferButton}
              disabled={isSubmitting}
            >
              <FaCheckCircle className={styles.buttonIcon} />
              {isSubmitting ? "Processing..." : "Complete Transfer"}
            </button>
          </form>
        )}

        {message && !isVerified && (
          <div className={styles.messageBox}>{message}</div>
        )}

        <div className={styles.instructions}>
          <h4>Instructions:</h4>
          <ol>
            <li>Check your email for the 6-digit verification code</li>
            <li>Enter the verification code above</li>
            <li>Set a new password for your arena account</li>
            <li>Click "Complete Transfer" to finalize the ownership change</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ArenaOwnershipTransfer;
